import React, { useCallback, useContext } from 'react';
import axios from 'axios';
import { CROPWISE_PROXY_V2_URL } from '../utilities/apiConstants';
import { getAccessToken } from '../utilities/base-auth';
import { Context } from '../components/Store';
import catchCancel from '../helpers/catchCancel';

// Enums matching backend
export enum PreferenceKey {
  OPERATION_TANK_INFORMATION = 'OPERATION_TANK_INFORMATION',
  OPERATION_PRODUCT = 'OPERATION_PRODUCT',
  OPERATION_WEATHER = 'OPERATION_WEATHER',
  OPERATION_PDF_ORIENTATION = 'OPERATION_PDF_ORIENTATION',
  PROPERTY_GROUP_BY = 'PROPERTY_GROUP_BY',
  WORK_ORDER_CONVERSION_ACTION = 'WORK_ORDER_CONVERSION_ACTION'
}

// API Types
export interface PreferenceResponse {
  id: string;
  preferenceKey: PreferenceKey;
  preferenceValue: any;
  orgId?: string;
  userId?: string;
  resourceId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  elements: number;
  page: number;
  limit: number;
  pages: number;
}

export interface PreferenceFilters {
  userId?: string;
  preferenceKey?: PreferenceKey;
  orgId?: string;
  resourceId?: string;
  limit?: number;
  offset?: number;
}

export interface CreatePreferenceRequest {
  preferenceKey: PreferenceKey;
  preferenceValue: any;
  orgId?: string;
  userId?: string;
  resourceId?: string;
}

export interface UpdatePreferenceRequest {
  preferenceValue: any;
}

export interface UpsertPreferenceRequest {
  preferenceKey: PreferenceKey;
  preferenceValue: any;
  orgId?: string;
  userId?: string;
  resourceId?: string;
}

// API base URL
const PREFERENCES_API_URL = `${CROPWISE_PROXY_V2_URL}/preferences`;

// Hook return type
export interface UsePreferencesReturn {
  getPreferences: (
    filters?: PreferenceFilters
  ) => Promise<PaginatedResponse<PreferenceResponse>>;
  getPreferenceById: (id: string) => Promise<PreferenceResponse>;
  createPreference: (
    request: CreatePreferenceRequest
  ) => Promise<PreferenceResponse>;
  updatePreference: (
    id: string,
    request: UpdatePreferenceRequest
  ) => Promise<PreferenceResponse>;
  deletePreference: (id: string) => Promise<void>;
  upsertPreference: (
    request: UpsertPreferenceRequest
  ) => Promise<PreferenceResponse>;
  findPreferenceByKey: (
    preferenceKey: PreferenceKey,
    filters?: Omit<PreferenceFilters, 'preferenceKey'>
  ) => Promise<PreferenceResponse | null>;
  findPreferencesByOrg: (
    orgId: string,
    filters?: Omit<PreferenceFilters, 'orgId'>
  ) => Promise<PaginatedResponse<PreferenceResponse>>;
  findPreferencesByUser: (
    userId: string,
    filters?: Omit<PreferenceFilters, 'userId'>
  ) => Promise<PaginatedResponse<PreferenceResponse>>;
  findPreferencesByResource: (
    resourceId: string,
    filters?: Omit<PreferenceFilters, 'resourceId'>
  ) => Promise<PaginatedResponse<PreferenceResponse>>;
}

/**
 * Custom hook for managing preferences through the preferences API
 * Provides CRUD operations and helper methods for preference management
 *
 * @example
 * ```typescript
 * const { getPreferences, createPreference } = usePreferences();
 *
 * // Get all preferences for current organization
 * const preferences = await getPreferences({ orgId: 'org-123' });
 *
 * // Create a new tank information preference
 * const tankPreference = await createPreference({
 *   preferenceKey: PreferenceKey.OPERATION_TANK_INFORMATION,
 *   preferenceValue: {
 *     toggleEnabled: true,
 *     tankSize: 100,
 *     overallRatePerAcre: 2.5
 *   },
 *   orgId: 'org-123'
 * });
 * ```
 */
export const usePreferences = (): UsePreferencesReturn => {
  // Get dispatch from context for error handling
  const [{ organization }] = (useContext(Context) as unknown) as [
    Record<string, any>,
    React.Dispatch<any>
  ];

  const getHeaders = useCallback(() => {
    return {
      'Content-Type': 'application/json',
      'cwf-context': JSON.stringify({
        organization_id: organization.id
      }),
      Authorization: `Bearer ${getAccessToken()}`
    };
  }, [organization.id]);

  /**
   * Get preferences with optional filters
   */
  const getPreferences = useCallback(
    (
      filters: PreferenceFilters = {}
    ): Promise<PaginatedResponse<PreferenceResponse>> => {
      return axios
        .get(PREFERENCES_API_URL, {
          headers: getHeaders(),
          params: filters
        })
        .then(res => res.data)
        .catch(catchCancel) as Promise<PaginatedResponse<PreferenceResponse>>;
    },
    [getHeaders]
  );

  /**
   * Get a specific preference by ID
   */
  const getPreferenceById = useCallback(
    (id: string): Promise<PreferenceResponse> => {
      return axios
        .get(`${PREFERENCES_API_URL}/${id}`, {
          headers: getHeaders()
        })
        .then(res => res.data)
        .catch(catchCancel) as Promise<PreferenceResponse>;
    },
    [getHeaders]
  );

  /**
   * Create a new preference
   */
  const createPreference = useCallback(
    (request: CreatePreferenceRequest): Promise<PreferenceResponse> => {
      return axios
        .post(PREFERENCES_API_URL, request, {
          headers: getHeaders()
        })
        .then(res => res.data)
        .catch(catchCancel) as Promise<PreferenceResponse>;
    },
    [getHeaders]
  );

  /**
   * Update an existing preference
   */
  const updatePreference = useCallback(
    (
      id: string,
      request: UpdatePreferenceRequest
    ): Promise<PreferenceResponse> => {
      return axios
        .put(`${PREFERENCES_API_URL}/${id}`, request, {
          headers: getHeaders()
        })
        .then(res => res.data)
        .catch(catchCancel) as Promise<PreferenceResponse>;
    },
    [getHeaders]
  );

  /**
   * Delete a preference
   */
  const deletePreference = useCallback(
    (id: string): Promise<void> => {
      return axios
        .delete(`${PREFERENCES_API_URL}/${id}`, {
          headers: getHeaders()
        })
        .then(() => undefined)
        .catch(catchCancel) as Promise<void>;
    },
    [getHeaders]
  );

  /**
   * Find preference by key with optional additional filters
   */
  const findPreferenceByKey = useCallback(
    async (
      preferenceKey: PreferenceKey,
      filters: Omit<PreferenceFilters, 'preferenceKey'> = {}
    ): Promise<PreferenceResponse | null> => {
      const searchFilters = {
        ...filters,
        preferenceKey
      };

      const response = await getPreferences(searchFilters);

      // Return first match or null if none found
      return response.data.length > 0 ? response.data[0] : null;
    },
    [getPreferences]
  );

  /**
   * Create or update preference (upsert operation)
   * First tries to find an existing preference, then creates or updates accordingly
   */
  const upsertPreference = useCallback(
    async (request: UpsertPreferenceRequest): Promise<PreferenceResponse> => {
      // Try to find existing preference by key and context
      const existingPreference = await findPreferenceByKey(
        request.preferenceKey,
        {
          userId: request.userId,
          orgId: request.orgId,
          resourceId: request.resourceId
        }
      );

      if (existingPreference) {
        // Update existing preference
        return updatePreference(existingPreference.id, {
          preferenceValue: request.preferenceValue
        });
      }
      // Create new preference
      return createPreference(request);
    },
    [createPreference, updatePreference, findPreferenceByKey]
  );

  /**
   * Find preferences by organization
   */
  const findPreferencesByOrg = useCallback(
    (
      orgId: string,
      filters: Omit<PreferenceFilters, 'orgId'> = {}
    ): Promise<PaginatedResponse<PreferenceResponse>> => {
      return getPreferences({
        ...filters,
        orgId
      });
    },
    [getPreferences]
  );

  /**
   * Find preferences by user
   */
  const findPreferencesByUser = useCallback(
    (
      userId: string,
      filters: Omit<PreferenceFilters, 'userId'> = {}
    ): Promise<PaginatedResponse<PreferenceResponse>> => {
      return getPreferences({
        ...filters,
        userId
      });
    },
    [getPreferences]
  );

  /**
   * Find preferences by resource
   */
  const findPreferencesByResource = useCallback(
    (
      resourceId: string,
      filters: Omit<PreferenceFilters, 'resourceId'> = {}
    ): Promise<PaginatedResponse<PreferenceResponse>> => {
      return getPreferences({
        ...filters,
        resourceId
      });
    },
    [getPreferences]
  );

  return {
    getPreferences,
    getPreferenceById,
    createPreference,
    updatePreference,
    deletePreference,
    upsertPreference,
    findPreferenceByKey,
    findPreferencesByOrg,
    findPreferencesByUser,
    findPreferencesByResource
  };
};

export default usePreferences;
